import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { SelectComponent } from "@components/select/select.component";
import { TranslateModule } from "@ngx-translate/core";
import { AccountSocialUpdateRequest } from "app/interfaces/account-social-update-request.interface";
import { AccountUpdateRequest } from "app/interfaces/account-update-request.interface";
import { UserRole } from "app/interfaces/api/UserRole";
import { SelectValue } from "app/interfaces/select-value.interface";
import { ApiService, UserInfo } from "app/services/api.service";
import { AccountType } from 'app/interfaces/api/AccountType';
import { Router } from '@angular/router';
import { take } from "rxjs";
import { LinkComponent } from "@components/link/link.component";
import { environment } from "environments/environment";

@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrl: './company.component.scss',
  imports: [CommonModule, TranslateModule, FormsModule, SelectComponent, ButtonComponent, LinkComponent],
  standalone: true,
})

export class CompanyComponent implements OnInit {
    userInfo: UserInfo;

    name: string = '';
    address: string = '';
    postalCode: string = '';
    city: string = '';
    state: string = '';
    country: string = '';
    phone: string = '';
    email: string = '';

    web: string = '';
    facebook: string = '';
    instagram: string = '';

    countryOptions: SelectValue[] = [];
    stateOptions: SelectValue[] = [];

    selectedCountryObject: SelectValue | null = null;
    selectedStateObject: SelectValue | null = null;

    originalBusinessInfo: AccountUpdateRequest;
    originalSocialInfo: AccountSocialUpdateRequest;

    hasChanges = false;

    constructor(private api: ApiService, private router: Router) {}

    protected readonly AccountType = AccountType;

    goToSalesSupport(): void {
        this.router.navigate(['/sales-support']);
    }

    determineCommissionState(): string {
        if (!this.userInfo) return 'UNKNOWN';

        const status = this.userInfo.supplierStatus || '';
        const hasSupplierId = !!this.userInfo.sapIdSupplier;

        if (!hasSupplierId && !status) return 'NO_PROJECT';
        if (status === 'Created from SAP') return 'INCOMPLETE_REGISTRATION';
        if (!hasSupplierId && status && status !== 'Created from SAP') return 'MUST_REGISTER';
        if (['Blocked', 'Requested', 'In Review', 'In Progress'].includes(status)) return 'UNDER_REVIEW';
        if (hasSupplierId) return 'ACTIVE_SUPPLIER';

        return 'UNKNOWN';
    }

    isActiveSupplier(): boolean {
        return this.determineCommissionState() === 'ACTIVE_SUPPLIER';
    }

    onVendorButtonClick(): void {
        if (this.isActiveSupplier()) {
            this.api.getExternalLinkToken('supplier').subscribe({
                next: ({ token }) => {
                    const baseUrl = environment.production
                        ? 'https://app.cosentino.com/supplier/privateTabs/datos-proveedores'
                        : 'https://devapp.cosentino.com/supplier/privateTabs/datos-proveedores'
                    const language = this.userInfo.language ?? ''
                    window.open(`${baseUrl}?sso=${encodeURIComponent(token)}&language=${encodeURIComponent(language)}`, '_blank') //NOSONAR
                },
                error: (err) => {
                    console.error('Error getting supplier token', err)
                    const baseUrl = environment.production
                        ? 'https://app.cosentino.com/supplier/privateTabs/datos-proveedores'
                        : 'https://devapp.cosentino.com/supplier/privateTabs/datos-proveedores'
                    window.open(baseUrl, '_blank') //NOSONAR
                }
            })
        } else {
            this.goToSalesSupport();
        }
    }

    onAgreementButtonClick(): void {
        this.api.getContractInfo('ctop').subscribe(contract => {
            window.open(contract.contractUrl, '_blank');
        })
    }

    ngOnInit(): void {
        this.api.userInfo.pipe(take(1)).subscribe((userInfo) => {
            this.userInfo = userInfo;
            this.name = this.userInfo.accountName ?? '';
            this.address = this.userInfo.accountStreet ?? '';
            this.postalCode = this.userInfo.accountPostalCode ?? '';
            this.city = this.userInfo.accountCity ?? '';
            this.phone = this.userInfo.accountPhone ?? '';
            this.email = this.userInfo.accountEmail ?? '';
            this.country = this.userInfo.country ?? '';
            this.state = this.userInfo.accountState ?? '';

            this.api.getCountryPicklist().subscribe((data) => {
                this.countryOptions = data.map(c => ({
                    id: c.value,
                    value: c.value,
                    label: c.label
                }));

                this.selectedCountryObject = this.countryOptions.find(c => c.value === this.country) ?? null;

                if (this.selectedCountryObject) {
                    this.loadStates(this.selectedCountryObject.value);
                }
            });

            this.originalBusinessInfo = {
                commercialName: this.name,
                billingStreet: this.address,
                billingPostalCode: this.postalCode,
                billingCity: this.city,
                billingStateCode: this.state,
                billingCountry: this.country,
                phone: this.phone,
                corporateEmail: this.email,
            };
        });

        this.api.getAccountSocialInfo().pipe(take(1)).subscribe({
            next: (socialInfo) => {
                this.web = socialInfo.website ?? '';
                this.facebook = socialInfo.facebook ?? '';
                this.instagram = socialInfo.instagram ?? '';

                this.originalSocialInfo = {
                    website: this.web,
                    instagram: this.instagram,
                    facebook: this.facebook,
                };
            },
            error: (err) => {
                console.error('Error al obtener redes sociales del account', err);
            }
        });
    }

    loadStates(countryCode: string): void {
        this.api.getStatePicklist(countryCode).subscribe((data) => {
            this.stateOptions = data.map(s => ({
                id: s.value,
                value: s.value,
                label: s.label
            }));

            if (this.state) {
                this.selectedStateObject = this.stateOptions.find(s => s.value === this.state) ?? null;
            }
        });
    }

    checkForChanges(): void {
        this.hasChanges = this.hasBusinessInfoChanged() || this.hasSocialInfoChanged();
    }


    onCountryChange(value: SelectValue | null): void {
        this.selectedCountryObject = value;
        this.country = value?.value ?? '';
        this.selectedStateObject = null;
        this.state = '';
        if (value?.value) {
            this.loadStates(value.value);
        }
        this.checkForChanges();
    }

    onStateChange(value: SelectValue | null): void {
        this.selectedStateObject = value;
        this.state = value?.value ?? '';
        this.checkForChanges();
    }

    hasBusinessInfoChanged(): boolean {
        return JSON.stringify(this.originalBusinessInfo) !== JSON.stringify({
            commercialName: this.name,
            billingStreet: this.address,
            billingPostalCode: this.postalCode,
            billingCity: this.city,
            billingStateCode: this.state,
            billingCountry: this.country,
            phone: this.phone,
            corporateEmail: this.email,
        });
    }

    hasSocialInfoChanged(): boolean {
        return JSON.stringify(this.originalSocialInfo) !== JSON.stringify({
            website: this.web,
            instagram: this.instagram,
            facebook: this.facebook,
        });
    }

    onSaveAll(): void {
        const updates: Promise<any>[] = [];

        let updatedBusinessInfo: AccountUpdateRequest | null = null;
        let updatedSocialInfo: AccountSocialUpdateRequest | null = null;

        if (this.hasBusinessInfoChanged()) {
            updatedBusinessInfo = {
                commercialName: this.name,
                billingStreet: this.address,
                billingPostalCode: this.postalCode,
                billingCity: this.city,
                billingStateCode: this.state,
                billingCountry: this.country,
                phone: this.phone,
                corporateEmail: this.email,
            };
            updates.push(this.api.updateAccountInfo(updatedBusinessInfo).toPromise());
        }

        if (this.hasSocialInfoChanged()) {
            updatedSocialInfo = {
                website: this.web,
                instagram: this.instagram,
                facebook: this.facebook,
            };
            updates.push(this.api.updateAccountSocialInfo(updatedSocialInfo).toPromise());
        }

        if (updates.length === 0) {
            console.log('No hay cambios que guardar');
            return;
        }

        Promise.all(updates)
            .then(([businessResult, socialResult]) => {
                console.log('Cambios guardados correctamente');

                if (businessResult === true && updatedBusinessInfo) {
                    this.originalBusinessInfo = updatedBusinessInfo;

                    const updatedUserInfo: UserInfo = {
                        ...this.userInfo,
                        accountName: this.name,
                        accountStreet: this.address,
                        accountPostalCode: this.postalCode,
                        accountCity: this.city,
                        accountPhone: this.phone,
                        accountEmail: this.email,
                        country: this.country,
                        accountState: this.state,
                    };

                    this.api.updateUserInfo(updatedUserInfo);
                } else {
                    this.name = this.userInfo.accountName ?? '';
                    this.address = this.userInfo.accountStreet ?? '';
                    this.postalCode = this.userInfo.accountPostalCode ?? '';
                    this.city = this.userInfo.accountCity ?? '';
                    this.state = this.userInfo.accountState ?? '';
                    this.country = this.userInfo.country ?? '';
                    this.phone = this.userInfo.accountPhone ?? '';
                    this.email = this.userInfo.accountEmail ?? '';

                    this.selectedCountryObject = this.countryOptions.find(c => c.value === this.userInfo.country) ?? null;

                    if (this.userInfo.country) {
                        this.api.getStatePicklist(this.userInfo.country).subscribe((data) => {
                            this.stateOptions = data.map(s => ({
                                id: s.value,
                                value: s.value,
                                label: s.label
                            }));

                            this.selectedStateObject = this.stateOptions.find(s => s.value === this.userInfo.accountState) ?? null;
                        });
                    }
                }


                if (socialResult === true && updatedSocialInfo) {
                    this.originalSocialInfo = updatedSocialInfo;
                }

                this.hasChanges = false;
            })
        .catch((err) => {
            console.error('Error al guardar cambios', err);
        });
    }
}
